import { createSubmitButtonBusyState } from './payment-methods/submit-button';
const handlerLoaders = {
    paypal: () => import('./payment-methods/paypal').then((m) => m.default),
    'paypal-donate': () => import('./payment-methods/paypal-donate').then((m) => m.default),
    stripe: () => import('./payment-methods/stripe').then((m) => m.default),
    'mock-pay': () => import('./payment-methods/mock-pay').then((m) => m.default),
};
const loadedHandlers = new Map();
const formStateMap = new Map();
const userCallbackMap = new Map();
function findJsForm(container) {
    if (formStateMap.has(container)) {
        return container;
    }
    return container.querySelector('.coredna-js-form') ?? undefined;
}
function getCachedHandler(el) {
    return loadedHandlers.get(el.dataset.paymentProvider ?? '');
}
function ensureInitialised(state, el) {
    if (state.initialised.has(el)) {
        return Promise.resolve();
    }
    if (state.activeInitPromise !== null) {
        return state.activeInitPromise;
    }
    const provider = el.dataset.paymentProvider ?? '';
    const loader = handlerLoaders[provider];
    if (!loader) {
        return Promise.resolve();
    }
    const promise = loader()
        .then((handler) => {
        loadedHandlers.set(provider, handler);
        return handler.init(el);
    })
        .then(() => {
        state.initialised.add(el);
        state.activeInitPromise = null;
    })
        .catch((err) => {
        state.activeInitPromise = null;
        throw err;
    });
    state.activeInitPromise = promise;
    return promise;
}
function activateTab(state, el, tabBtn, tabContainer) {
    tabContainer.querySelectorAll('.nav-link').forEach((l) => l.classList.remove('active'));
    tabBtn.classList.add('active');
    state.elements.forEach((paymentEl) => {
        paymentEl.style.display = paymentEl === el ? '' : 'none';
    });
    const previousEl = state.activeEl;
    state.activeEl = el;
    if (previousEl !== el) {
        const previousHandler = getCachedHandler(previousEl);
        const destroyPromise = previousHandler?.destroy ? previousHandler.destroy() : Promise.resolve();
        destroyPromise
            .then(() => {
            state.initialised.delete(previousEl);
        })
            .catch((err) => {
            console.error(`Payment handler "${previousEl.dataset.paymentProvider}" destroy failed:`, err);
            state.initialised.delete(previousEl);
        });
    }
    ensureInitialised(state, el).catch((err) => console.error(`Payment handler "${el.dataset.paymentProvider}" failed:`, err));
}
function buildTabNav(state) {
    const firstEl = state.elements[0];
    const parent = firstEl.parentElement;
    if (!parent) {
        return;
    }
    const ul = document.createElement('ul');
    ul.className = 'nav nav-tabs mb-3';
    state.elements.forEach((el, index) => {
        const nameDiv = el.querySelector('.payment-method-name');
        const label = nameDiv?.textContent?.trim() ?? el.dataset.paymentProvider ?? 'Pay';
        if (nameDiv) {
            nameDiv.classList.add('d-none');
        }
        if (index > 0) {
            el.style.display = 'none';
        }
        const li = document.createElement('li');
        li.className = 'nav-item';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'nav-link' + (index === 0 ? ' active' : '');
        btn.textContent = label;
        btn.addEventListener('click', () => activateTab(state, el, btn, ul));
        li.appendChild(btn);
        ul.appendChild(li);
    });
    parent.insertBefore(ul, firstEl);
}
function collectBillingAddress(container) {
    const fieldset = container.querySelector('.payment-billing-address');
    if (!fieldset) {
        return {};
    }
    const address = {};
    fieldset.querySelectorAll('input, select').forEach((input) => {
        const match = input.name.match(/\[billingAddress]\[(\w+)]$/);
        if (match) {
            address[match[1]] = input.value;
        }
    });
    return address;
}
async function collectPaymentFormData(callbackData, jsForm, state) {
    const data = callbackData.data;
    let merged = data;
    if (state) {
        if (state.activeInitPromise) {
            await state.activeInitPromise;
        }
        if (state.initialised.has(state.activeEl)) {
            const handler = getCachedHandler(state.activeEl);
            if (handler) {
                state.busy?.end();
                state.busy = createSubmitButtonBusyState(callbackData.currentStep);
                state.busy.begin();
                let paymentData;
                try {
                    paymentData = await handler.getPaymentData();
                }
                catch {
                    throw new Error('Payment processing failed.');
                }
                merged = { ...data, ...paymentData };
                const elementName = state.activeEl.dataset.elementName;
                if (elementName && merged[elementName] && typeof merged[elementName] === 'object') {
                    merged[elementName].billingAddress = collectBillingAddress(jsForm ?? callbackData.formContainer);
                }
            }
        }
    }
    const userModifierName = jsForm ? userCallbackMap.get(jsForm)?.modifier : undefined;
    if (userModifierName) {
        const userCallback = window[userModifierName];
        if (typeof userCallback === 'function') {
            const userResult = await userCallback({
                ...callbackData,
                data: merged,
            });
            if (userResult) {
                return userResult;
            }
        }
    }
    return merged;
}
window.corednaJsFormPaymentModifier = async (callbackData) => {
    const jsForm = findJsForm(callbackData.formContainer);
    const state = jsForm ? formStateMap.get(jsForm) : undefined;
    try {
        return await collectPaymentFormData(callbackData, jsForm, state);
    }
    catch (err) {
        state?.busy?.end();
        throw err;
    }
};
function isSuccessfulSubmit(xhr) {
    return xhr.status >= 200 && xhr.status < 300 && xhr.status !== 207;
}
window.corednaJsFormPaymentResult = async (callbackData) => {
    const response = callbackData.xhr.response;
    const jsForm = findJsForm(callbackData.formContainer);
    const state = jsForm ? formStateMap.get(jsForm) : undefined;
    if (!isSuccessfulSubmit(callbackData.xhr) || response?.paypalDonateConfig) {
        state?.busy?.end();
    }
    if (response?.redirectUrl) {
        window.location.href = response.redirectUrl;
        return false;
    }
    if (response?.paypalDonateConfig) {
        const handler = state ? getCachedHandler(state.activeEl) : undefined;
        if (handler?.receiveSdkConfig) {
            await handler.receiveSdkConfig(response.paypalDonateConfig, callbackData.currentStep, callbackData.resultStep);
        }
        return false;
    }
    const userResultName = jsForm ? userCallbackMap.get(jsForm)?.result : undefined;
    if (userResultName) {
        const userCallback = window[userResultName];
        if (typeof userCallback === 'function') {
            const userResult = await userCallback(callbackData);
            return userResult;
        }
    }
    return true;
};
function init() {
    const formToEls = new Map();
    document.querySelectorAll('[data-payment-provider]').forEach((el) => {
        const provider = el.dataset.paymentProvider;
        if (!provider || !handlerLoaders[provider]) {
            return;
        }
        const container = el.closest('.coredna-js-form') ?? el.closest('form') ?? el.parentElement;
        if (!container) {
            return;
        }
        if (!formToEls.has(container)) {
            formToEls.set(container, []);
        }
        formToEls.get(container).push(el);
    });
    formToEls.forEach((els, container) => {
        const state = {
            elements: els,
            activeEl: els[0],
            activeInitPromise: null,
            initialised: new Set(),
            busy: null,
        };
        formStateMap.set(container, state);
        if (container.classList.contains('coredna-js-form')) {
            const existingModifier = container.dataset.submitModifierCallback;
            const existingResult = container.dataset.submitResultCallback;
            const userCallbacks = {};
            if (existingModifier && existingModifier !== 'corednaJsFormPaymentModifier') {
                userCallbacks.modifier = existingModifier;
            }
            if (existingResult && existingResult !== 'corednaJsFormPaymentResult') {
                userCallbacks.result = existingResult;
            }
            userCallbackMap.set(container, userCallbacks);
            container.dataset.submitModifierCallback = 'corednaJsFormPaymentModifier';
            container.dataset.submitResultCallback = 'corednaJsFormPaymentResult';
        }
        if (els.length > 1) {
            buildTabNav(state);
        }
        ensureInitialised(state, els[0]).catch((err) => console.error(`Payment handler "${els[0].dataset.paymentProvider}" failed:`, err));
    });
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
}
else {
    init();
}
